import React, { useEffect, useState } from 'react';
import { HiOutlineDocumentText } from 'react-icons/hi2';
import { FaInbox } from 'react-icons/fa';
import { formatCurrency } from '../../../../../../../../utils/functions';
import TableDateFormatter from '../../../../../../../../components/common/Table/TableDateFormatter';
import MovementDetailsModal from './components/MovementDetailsModal';

interface CajaMovement {
  caja_movement_id: number;
  workstation_id: number;
  workstation_name: string;
  user_id: number;
  user_name: string;
  before_amount:number;
  after_amount:number;
  amount: number;
  description: string | null;
  is_positive: boolean;
  caja_movement_type_id: number;
  caja_movement_type_name: string;
  caja_movement_metadata: any;
  timestamp: string;
  local_timestamp: string;
  session_id?: number;
  shift_id?: number | null;
  shift_name?: string | null;
  receipt_id?: number | null;
}

interface TableProps {
  data: CajaMovement[];
}

const Table: React.FC<TableProps> = ({ data }) => {
  const [isMobile, setIsMobile] = useState(false);
  const [selectedMovement, setSelectedMovement] = useState<CajaMovement | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 900);
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Check initial size

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const isValidTimestamp = (timestamp: string | null | undefined): boolean => {
    if (!timestamp || timestamp === '') return false;
    const date = new Date(timestamp);
    return !isNaN(date.getTime());
  };

  const handleViewDetails = (movement: CajaMovement) => {
    setSelectedMovement(movement);
    setShowDetailsModal(true);
  };

  // Render mobile card view
  const renderMobileView = () => (
    <div className="flex flex-col gap-2.5 p-2">
      {data.map(m => (
        <div
          key={m.caja_movement_id}
          className={`mp-mobile-card ${m.is_positive ? 'mp-mobile-card-accent-success' : 'mp-mobile-card-accent-danger'}`}
          onClick={() => handleViewDetails(m)}
        >
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-sm font-semibold" style={{ color: 'var(--text-color)' }}>{m.caja_movement_type_name}</span>
            <span className="text-xs opacity-40">
              {isValidTimestamp(m.local_timestamp) ? (
                <TableDateFormatter
                  data={m.local_timestamp}
                  isRealTime={false}
                  stopCounting
                  isTimestamp
                />
              ) : (
                'N/D'
              )}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <span className="text-sm opacity-60" style={{ color: 'var(--text-color)' }}>{m.user_name}</span>
              <span className={`mp-movement-badge ml-2 ${m.is_positive ? 'mp-movement-badge-positive' : 'mp-movement-badge-negative'}`}>
                <span className="opacity-50">{formatCurrency(m.before_amount)}</span>
                <span className="mp-movement-arrow">→</span>
                <span className={`font-semibold ${m.is_positive ? 'text-emerald-500' : 'text-red-500'}`}>{formatCurrency(m.after_amount)}</span>
              </span>
            </div>
            <span className={`text-base font-bold tabular-nums ${m.is_positive ? 'text-emerald-500' : 'text-red-500'}`}>{formatCurrency(m.amount)}</span>
          </div>
        </div>
      ))}
    </div>
  );

  // Render desktop table view
  const renderDesktopView = () => (
    <div className="mp-table-card">
      <div className="overflow-x-auto">
        <table className="mp-table">
          <thead className="mp-table-head">
            <tr>
              <th>Caja</th>
              <th>Acción</th>
              <th>Usuario</th>
              <th>Turno</th>
              <th>Fecha</th>
              <th>Movimiento</th>
              <th>Monto</th>
              <th style={{ textAlign: 'right' }}>Acciones</th>
            </tr>
          </thead>
          <tbody className="mp-table-body">
            {data.map(m => (
              <tr key={m.caja_movement_id}>
                <td className="font-medium">{m.workstation_name}</td>
                <td><span style={{ color: '#FC7556' }} className="font-medium">{m.caja_movement_type_name}</span></td>
                <td>{m.user_name}</td>
                <td className="text-xs opacity-50">{m.shift_name || 'N/A'}</td>
                <td className="opacity-50">
                  {isValidTimestamp(m.local_timestamp) ? (
                    <TableDateFormatter
                      data={m.local_timestamp}
                      isRealTime={false}
                      stopCounting
                      isTimestamp
                    />
                  ) : (
                    'N/D'
                  )}
                </td>
                <td>
                  <span className={`mp-movement-badge ${m.is_positive ? 'mp-movement-badge-positive' : 'mp-movement-badge-negative'}`}>
                    <span className="opacity-50">{formatCurrency(m.before_amount)}</span>
                    <span className="mp-movement-arrow">→</span>
                    <span className={`font-semibold ${m.is_positive ? 'text-emerald-500' : 'text-red-500'}`}>{formatCurrency(m.after_amount)}</span>
                  </span>
                </td>
                <td className="font-semibold tabular-nums">{formatCurrency(m.amount)}</td>
                <td style={{ textAlign: 'right' }}>
                  <button className="mp-btn-icon" onClick={() => handleViewDetails(m)} title="Ver Detalles">
                    <HiOutlineDocumentText size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mp-table-footer">
        <span className="text-xs opacity-35">{data.length} movimientos</span>
      </div>
    </div>
  );

  return (
    <>
      {data.length === 0 ? (
        <div className="mp-empty-state">
          <FaInbox className="mp-empty-state-icon" />
          <p className="mp-empty-state-text">No hay ningún movimiento aún!</p>
        </div>
      ) : (
        <>
          {isMobile ? renderMobileView() : renderDesktopView()}
        </>
      )}

      {selectedMovement && (
        <MovementDetailsModal
          show={showDetailsModal}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedMovement(null);
          }}
          movement={selectedMovement}
        />
      )}
    </>
  );
};

export default Table;
